/**
 * Which move run a Compacting one-run page reads — `?moveRunId=` (bata-reqs ADR 9002 stage 3).
 *
 * The runs come from `GET /compacting/runs` (platform ADR 9050), newest first, one bounded page: the
 * platform's own order, completed and refused runs alike. With no run in the URL the newest is opened
 * and written into the URL with `replaceState` (DEC-99, `docs/adr/9039`); a run the URL names always
 * wins; «Il più recente» drops the run so the page follows the newest again. An empty directory is a
 * finding — the one run has not run yet — and says so.
 *
 * A run a later run of the same market and ship-week superseded is offered and says so in its label:
 * its lines can no longer be decided (409 `move_line_superseded`), and a planner should know before
 * opening it.
 */

import type { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApiQuery } from '@/api';
import { listMoveRuns, type MoveRunSummary } from '@/api/generated';
import { formatDate } from '@/lib/format';
import { Button, Select } from '@/primitives';
import {
  AsyncRegion, NEWEST_RUN_OPTION, RunDefaultNote, SCOPE_CHOOSER_CONTROL, ScopeChooser, useRunDefault, type UseRunDefault,
} from '@/planning';

import { MOVE_RUN_ID_PARAM, MOVE_RUN_SCOPE } from './move-run';

/** Runs the picker offers: one page, newest first (the eligibility list reads no digest, ADR 9039). */
export const MOVE_RUN_LIST_LIMIT = 50;

/**
 * The LIVE move-run directory's first page — `GET /compacting/runs` lists live runs only by default (platform
 * v0.126.0, #904): a simulation never stands among them, so the newest-run default never opens one. Shared by
 * the picker and the pages, one cached query.
 */
export function useMoveRuns() {
  return useApiQuery(listMoveRuns, { query: { limit: MOVE_RUN_LIST_LIMIT } });
}

/** The simulations of the one run (`simulation=true`), apart and never mixed with the live runs. */
export function useSimulatedMoveRuns(enabled = true) {
  return useApiQuery(listMoveRuns, { query: { simulation: true, limit: MOVE_RUN_LIST_LIMIT } }, { enabled });
}

/** Whether a run is a simulation of the one run: `simulation_id` is null on a live run. Pure. */
export function isSimulatedMoveRun(run: Pick<MoveRunSummary, 'simulation_id'> | undefined): boolean {
  return run !== undefined && run.simulation_id !== null;
}

/**
 * The run the URL names: from the live directory, else — only when that does not list it — from the
 * simulations, so a link to a simulated run opens it. `undefined` while unread or when neither lists it.
 */
export function useMoveRun(runId: string | null): MoveRunSummary | undefined {
  const runs = useMoveRuns();
  const live = runId === null ? undefined : runs.data?.items.find((row) => row.run_id === runId);
  const simulated = useSimulatedMoveRuns(runId !== null && runs.data !== undefined && live === undefined);
  return live ?? (runId === null ? undefined : simulated.data?.items.find((row) => row.run_id === runId));
}

/** Write `?moveRunId=` — replacing the history entry for a run chosen for the planner, pushing one otherwise. */
function writeRun(runId: string | null, replace: boolean): void {
  if (typeof window === 'undefined') return;
  const next = new URLSearchParams(window.location.search);
  if (runId === null) next.delete(MOVE_RUN_ID_PARAM);
  else next.set(MOVE_RUN_ID_PARAM, runId);
  // A new run is a new list: the row the old one had open is not a row of this one.
  next.delete('row');
  const query = next.toString();
  const url = query === '' ? window.location.pathname : `?${query}`;
  if (replace) window.history.replaceState(window.history.state, '', url);
  else window.history.pushState({}, '', url);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** DEC-99 for a one-run page: the newest run of the directory, the platform's order. */
export function useMoveRunDefault(urlRunId: string | null): UseRunDefault {
  const runs = useMoveRuns();
  const offered = runs.isError ? [] : runs.data?.items.map((row) => row.run_id);
  return useRunDefault({ scope: MOVE_RUN_SCOPE, urlRunId, offered, write: (runId) => writeRun(runId, true) });
}

/** A run in words — «CO · settimana 2026-W44 · stock al 29 ott 2026» — from the directory row. */
export function moveRunLabel(t: TFunction, row: MoveRunSummary, language: string): string {
  const stock = row.stock_as_of_date === null ? t('absent.word.notStated') : formatDate(row.stock_as_of_date, language, 'UTC');
  return t(isSimulatedMoveRun(row) ? 'inseason.compacting.run.optionSimulated' : row.superseded ? 'inseason.compacting.run.optionSuperseded' : 'inseason.compacting.run.option', {
    country: row.country_key, week: row.ship_week, stock, lines: row.lines,
  });
}

export default function MoveRunPicker({ runId }: { readonly runId: string | null }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const runs = useMoveRuns();
  const memory = useMoveRunDefault(runId);
  const named = useMoveRun(runId);
  const simulations = useSimulatedMoveRuns();
  const [chosen, setChosen] = useState(runId ?? '');
  useEffect(() => { setChosen(runId ?? ''); }, [runId]);

  return (
    <ScopeChooser titleKey="inseason.compacting.run.title" aboutKey={['inseason.compacting.run.explainer']}>
      <AsyncRegion
        query={runs}
        errorTitleKey="inseason.compacting.run.loadFailed"
        emptyTitleKey="inseason.compacting.run.emptyTitle"
        emptyBodyKey="inseason.compacting.run.emptyBody"
        isEmpty={(page) => page.items.length === 0}
        loadingRows={1}
      >
        {(page) => {
          // A simulated run the URL names is offered, labelled as a simulation; the live list never holds one.
          const rows = named !== undefined && isSimulatedMoveRun(named) ? [named, ...page.items] : page.items;
          const options = [
            { value: NEWEST_RUN_OPTION, labelKey: 'runDefault.newestOption' },
            ...rows.map((row) => ({ value: row.run_id, label: moveRunLabel(t, row, i18n.language) })),
          ];
          const newest = chosen === NEWEST_RUN_OPTION;
          const highlighted = rows.find((row) => row.run_id === chosen);
          const simulated = simulations.data?.items ?? [];
          const open = (): void => {
            if (newest) {
              memory.chooseNewest();
              writeRun(null, false);
              return;
            }
            if (highlighted === undefined) return;
            memory.choose(highlighted.run_id);
            writeRun(highlighted.run_id, false);
          };
          return (
            <>
              <div className={SCOPE_CHOOSER_CONTROL}>
                <Select
                  labelKey="inseason.compacting.run.run"
                  labelHidden
                  placeholderKey="inseason.compacting.run.runPlaceholder"
                  options={options}
                  value={chosen}
                  onValueChange={setChosen}
                />
              </div>
              <Button variant="primary" onClick={open} disabled={newest ? false : highlighted === undefined || highlighted.run_id === runId}>
                {t('inseason.compacting.run.load')}
              </Button>
              <RunDefaultNote memoryKey={memory.key} runId={runId} />
              {/* The simulations, apart (platform v0.126.0: never mixed with the live runs): one opens on choice. */}
              {simulated.length === 0 ? null : (
                <div className={SCOPE_CHOOSER_CONTROL} data-move-run-simulations="">
                  <Select
                    labelKey="inseason.compacting.run.simulations"
                    labelHidden
                    placeholderKey="inseason.compacting.run.simulationsPlaceholder"
                    options={simulated.map((row) => ({ value: row.run_id, label: moveRunLabel(t, row, i18n.language) }))}
                    value={isSimulatedMoveRun(named) ? runId ?? '' : ''}
                    onValueChange={(id) => { memory.choose(id); writeRun(id, false); }}
                  />
                </div>
              )}
            </>
          );
        }}
      </AsyncRegion>
    </ScopeChooser>
  );
}
